import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

// Storage keys
const TOKEN_KEY = 'b2b_token';
const USER_KEY = 'b2b_user';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionValidated, setSessionValidated] = useState(false);

  // Validate session with backend on initial mount
  useEffect(() => {
    const validateSession = async () => {
      try {
        const savedToken = localStorage.getItem(TOKEN_KEY);
        
        if (!savedToken) {
          console.log('[Auth] No token found');
          setSessionValidated(true);
          setIsLoading(false);
          return;
        }

        console.log('[Auth] Validating session with backend...');
        
        // Validate token with backend
        const response = await authAPI.validateSession(savedToken);
        
        if (response.data.valid && response.data.user) {
          const validatedUser = response.data.user;
          console.log('[Auth] Session valid:', validatedUser.username, 'role:', validatedUser.role);
          setUser(validatedUser);
          setToken(savedToken);
          localStorage.setItem(USER_KEY, JSON.stringify(validatedUser));
        } else {
          // Invalid session - clear storage
          console.log('[Auth] Session invalid, clearing storage');
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        }
      } catch (error) {
        console.error('[Auth] Session validation failed:', error.response?.data?.detail || error.message);
        // Clear invalid session
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
      setSessionValidated(true);
      setIsLoading(false);
    };
    
    validateSession();
  }, []);

  const login = async (identifier, password, loginType = 'normal') => {
    try {
      const response = await authAPI.login(identifier, password, loginType);
      const { user: loggedInUser, token: sessionToken } = response.data;
      
      console.log('[Auth] Login successful:', loggedInUser.username, 'role:', loggedInUser.role);
      
      // Store token and user
      setToken(sessionToken);
      setUser(loggedInUser);
      localStorage.setItem(TOKEN_KEY, sessionToken);
      localStorage.setItem(USER_KEY, JSON.stringify(loggedInUser));
      
      return { success: true, user: loggedInUser };
    } catch (error) {
      const message = error.response?.data?.detail || 'Giriş başarısız!';
      console.log('[Auth] Login failed:', message);
      return { success: false, message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.detail || 'Kayıt başarısız!';
      return { success: false, message };
    }
  };

  const logout = useCallback(async () => {
    console.log('[Auth] Logging out');
    
    // Call backend logout to invalidate session
    const currentToken = localStorage.getItem(TOKEN_KEY);
    if (currentToken) {
      try {
        await authAPI.logout(currentToken);
      } catch (error) {
        console.error('[Auth] Logout API error:', error);
      }
    }
    
    // Clear local state
    setUser(null);
    setToken(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }, []);

  const updateUser = (updatedData) => {
    const updated = { ...user, ...updatedData };
    setUser(updated);
    localStorage.setItem(USER_KEY, JSON.stringify(updated));
  };

  // Get current token for API calls
  const getToken = useCallback(() => {
    return token || localStorage.getItem(TOKEN_KEY);
  }, [token]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token,
      login, 
      register, 
      logout, 
      updateUser, 
      isLoading,
      sessionValidated,
      getToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
